import { formatDistanceToNow } from 'date-fns'
import { RefreshCw, RotateCcw } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { HealthDot, HEALTH_LABELS } from './HealthDot'
import type { IntegrationOverviewCredential } from '@/types/integrations'

interface ProviderLocationCardProps {
  credential: IntegrationOverviewCredential
  masterActive: boolean
  onToggle: (credentialId: string, isActive: boolean) => void
  onSync: (credentialId: string) => void
  isToggling?: boolean
  isSyncing?: boolean
}

export function ProviderLocationCard({
  credential,
  masterActive,
  onToggle,
  onSync,
  isToggling,
  isSyncing,
}: ProviderLocationCardProps) {
  const locationName = credential.locations?.name ?? 'Unknown'
  const locationCode = credential.locations?.location_code ?? ''
  const isCircuitBroken = credential.health_status === 'circuit_broken'

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold truncate">
                {locationName}
                {locationCode && (
                  <span className="text-muted-foreground ml-1 font-normal">({locationCode})</span>
                )}
              </span>
            </div>

            <div className="flex items-center gap-3 mt-1.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5">
                      <HealthDot status={credential.health_status} />
                      <span className="text-xs text-muted-foreground">
                        {HEALTH_LABELS[credential.health_status]}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-xs">
                      {credential.consecutive_failures > 0
                        ? `${credential.consecutive_failures} consecutive failure${credential.consecutive_failures !== 1 ? 's' : ''}`
                        : 'No recent errors'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {credential.last_successful_sync ? (
                <span className="text-xs text-muted-foreground">
                  Last sync: {formatDistanceToNow(new Date(credential.last_successful_sync), { addSuffix: true })}
                </span>
              ) : credential.is_active ? (
                <span className="text-xs text-muted-foreground">Never synced</span>
              ) : null}
            </div>

            {credential.last_error && credential.consecutive_failures > 0 && (
              <p className="text-xs text-destructive mt-1.5 truncate max-w-[400px]">
                {credential.last_error}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isCircuitBroken ? (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => onSync(credential.id)}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3 w-3 mr-1.5" />
                )}
                Reset & Retry
              </Button>
            ) : credential.is_active ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSync(credential.id)}
                disabled={isSyncing}
              >
                <RefreshCw className={`h-3 w-3 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
                Sync Now
              </Button>
            ) : null}

            <Switch
              checked={credential.is_active}
              onCheckedChange={(checked) => onToggle(credential.id, checked)}
              disabled={isToggling || !masterActive}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
